// src/services/questionService.ts
// ✅ ATUALIZADO: countQuestions + suporte a filtros por ID

import { Question, QuestionFilters, QuestionWithProgress, CategoryNode, UserQuestionStats } from '../types/question.types';

const API_BASE_URL = '/api';

class QuestionService {
    private getHeaders(): HeadersInit {
        const token = localStorage.getItem('accessToken');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }

    private buildParams(filters: QuestionFilters): URLSearchParams {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });
        return params;
    }

    // ========== USUÁRIO ==========

    async getQuestions(filters: QuestionFilters = {}): Promise<{
        questions: QuestionWithProgress[];
        pagination: { total: number; page: number; limit: number; pages: number };
    }> {
        const params = this.buildParams(filters);
        const response = await fetch(`${API_BASE_URL}/questions?${params}`, { headers: this.getHeaders() });
        if (!response.ok) throw new Error('Erro ao buscar questões');
        return await response.json();
    }

    async getQuestionIds(filters: QuestionFilters = {}): Promise<{ ids: string[]; total: number }> {
        const params = this.buildParams(filters);
        const response = await fetch(`${API_BASE_URL}/questions/ids?${params}`, { headers: this.getHeaders() });
        if (!response.ok) throw new Error('Erro ao buscar IDs');
        return await response.json();
    }

    /**
     * Contar questões para preview de filtro
     */
    async countQuestions(filters: QuestionFilters = {}): Promise<number> {
        const params = this.buildParams(filters);
        const response = await fetch(`${API_BASE_URL}/questions/count?${params}`, { headers: this.getHeaders() });
        if (!response.ok) return 0;
        const data = await response.json();
        return data.count ?? 0;
    }

    async getQuestion(questionId: string): Promise<{
        question: Question;
        userProgress: any;
        stats: any;
    }> {
        const response = await fetch(`${API_BASE_URL}/questions/${questionId}`, { headers: this.getHeaders() });
        if (!response.ok) throw new Error('Erro ao buscar questão');
        return await response.json();
    }

    async answerQuestion(questionId: string, selectedAlternative: 'A' | 'B' | 'C' | 'D' | 'E'): Promise<{
        isCorrect: boolean;
        correctAlternative: string;
        explanation: string | null;
        videoUrl: string | null;
        stats: any;
        userProgress: any;
    }> {
        const response = await fetch(`${API_BASE_URL}/questions/${questionId}/answer`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ selectedAlternative })
        });
        if (!response.ok) throw new Error('Erro ao responder questão');
        return await response.json();
    }

    async getNextQuestion(questionId: string): Promise<{ nextQuestionId: string | null; isLastQuestion: boolean }> {
        const response = await fetch(`${API_BASE_URL}/questions/${questionId}/next`, { headers: this.getHeaders() });
        if (!response.ok) throw new Error('Erro ao buscar próxima questão');
        return await response.json();
    }

    async getPreviousQuestion(questionId: string): Promise<{ previousQuestionId: string | null; isFirstQuestion: boolean }> {
        const response = await fetch(`${API_BASE_URL}/questions/${questionId}/previous`, { headers: this.getHeaders() });
        if (!response.ok) throw new Error('Erro ao buscar questão anterior');
        return await response.json();
    }

    async getUserStats(): Promise<UserQuestionStats> {
        const response = await fetch(`${API_BASE_URL}/questions/stats/user`, { headers: this.getHeaders() });
        if (!response.ok) throw new Error('Erro ao buscar estatísticas');
        const data = await response.json();
        return data.stats;
    }

    async reportQuestionError(questionId: string, description: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/questions/${questionId}/report-error`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ description })
        });
        if (!response.ok) throw new Error('Erro ao enviar reporte');
    }

    // ========== ADMIN ==========

    async getQuestionsAdmin(filters: any = {}): Promise<any> {
        const params = this.buildParams(filters);
        const response = await fetch(`${API_BASE_URL}/admin/questions?${params}`, { headers: this.getHeaders() });
        if (!response.ok) throw new Error('Erro ao buscar questões');
        return await response.json();
    }

    async getQuestionsStatsOverview(filters: any = {}): Promise<any> {
        const params = this.buildParams(filters);
        const response = await fetch(`${API_BASE_URL}/admin/questions/stats-overview?${params}`, {
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Erro ao buscar estatísticas');
        return await response.json();
    }

    async createQuestion(questionData: any): Promise<Question> {
        const response = await fetch(`${API_BASE_URL}/admin/questions`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(questionData)
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao criar questão');
        }
        const data = await response.json();
        return data.question;
    }

    async updateQuestion(questionId: string, updates: any): Promise<Question> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/${questionId}`, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: JSON.stringify(updates)
        });
        if (!response.ok) throw new Error('Erro ao atualizar questão');
        const data = await response.json();
        return data.question;
    }

    async deleteQuestion(questionId: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/${questionId}`, {
            method: 'DELETE',
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Erro ao deletar questão');
    }

    async togglePublish(questionId: string): Promise<{ status: string }> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/${questionId}/toggle-publish`, {
            method: 'PATCH',
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Erro ao alterar status');
        return await response.json();
    }

    async getCategoriesTree(): Promise<CategoryNode[]> {
        const response = await fetch(`${API_BASE_URL}/questions/categories/tree`, { headers: this.getHeaders() });
        if (!response.ok) throw new Error('Erro ao buscar categorias');
        const data = await response.json();
        return data.categories;
    }

    async createCategory(categoryData: {
        name: string;
        level: number;
        parentId?: string | null;
        description?: string;
    }): Promise<any> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/categories`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(categoryData)
        });
        if (!response.ok) throw new Error('Erro ao criar categoria');
        return await response.json();
    }

    async updateCategory(categoryId: string, name: string): Promise<any> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/categories/${categoryId}`, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: JSON.stringify({ name })
        });
        if (!response.ok) throw new Error('Erro ao editar categoria');
        return await response.json();
    }

    async getQuestionAdmin(questionId: string): Promise<{
        question: Question;
        userProgress: any;
        stats: any;
    }> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/${questionId}`, {
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Erro ao buscar questão');
        return await response.json();
    }

    async setQuestionStatus(questionId: string, status: 'rascunho' | 'publicado' | 'analise'): Promise<{ status: string }> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/${questionId}/set-status`, {
            method: 'PATCH',
            headers: this.getHeaders(),
            body: JSON.stringify({ status })
        });
        if (!response.ok) throw new Error('Erro ao definir status');
        return await response.json();
    }

    async submitAnalysis(questionId: string, data: {
        suggestedDifficulty?: 'facil' | 'medio' | 'dificil' | null;
        suggestedCategory?: { level1?: string; level2?: string; level3?: string; level4?: string };
        improvementText?: string;
        isCorrect?: boolean | null;
        shouldDelete?: boolean | null;
        shouldRedo?: 'totalmente' | 'parcialmente' | 'nao' | null;
        canPublish?: boolean | null;
        suggestedStatement?: string;
        suggestedAlternatives?: { letter: string; text: string }[];
    }): Promise<any> {
        const response = await fetch(`${API_BASE_URL}/questions/${questionId}/analysis`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Erro ao enviar análise');
        return await response.json();
    }

    async getQuestionAnalyses(questionId: string): Promise<any[]> {
        const response = await fetch(`${API_BASE_URL}/admin/analysis/${questionId}/analyses`, {
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Erro ao buscar análises');
        const data = await response.json();
        return data.analyses;
    }

    async getAnalysisQuestions(page = 1, limit = 20): Promise<any> {
        const response = await fetch(`${API_BASE_URL}/admin/analysis/questions?page=${page}&limit=${limit}`, {
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Erro ao buscar questões em análise');
        return await response.json();
    }

    async importQuestions(
        questions: any[]
    ): Promise<{ success: boolean; message: string; results: { imported: number; failed: number; errors: any[] } }> {
        const response = await fetch(`${API_BASE_URL}/admin/import/questions`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ questions })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao importar questões');
        }
        return await response.json();
    }

    async deleteCategory(categoryId: string): Promise<{ affectedCount: number; message: string }> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/categories/${categoryId}`, {
            method: 'DELETE',
            headers: this.getHeaders()
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao deletar categoria');
        }
        return await response.json();
    }

    async getCategoryQuestionCount(categoryId: string): Promise<number> {
        const response = await fetch(`${API_BASE_URL}/admin/questions/categories/${categoryId}/question-count`, {
            headers: this.getHeaders()
        });
        if (!response.ok) return 0;
        const data = await response.json();
        return data.count ?? 0;
    }

    async getReviewerRanking(period: 'total' | 'mes' | 'semana' | 'hoje' = 'total'): Promise<any[]> {
        const response = await fetch(`${API_BASE_URL}/admin/analysis/ranking?period=${period}`, {
            headers: this.getHeaders()
        });
        if (!response.ok) throw new Error('Erro ao buscar ranking');
        const data = await response.json();
        return data.ranking;
    }

    async bulkSetStatus(ids: string[], status: 'rascunho' | 'publicado' | 'analise' | 'prova'): Promise<void> {
        await Promise.all(ids.map(id => this.setQuestionStatus(id, status as any)));
    }
}

export const questionService = new QuestionService();